/**
 * Skill Cards (section 3006, variant 3000) — PRD Figma 2015:3259, frames:
 * MV 2000:6500 · 1080 2000:6458 · 1230 2045:3866 · 1430 2045:3914 · 1750 2045:4020.
 *
 * Containers are metadata rows (min 2 / max 10). Per PRD, each row needs at
 * least ONE of title / subtitle / paragraph — the table can't validate that
 * cross-field rule, so the widget skips rows that have none.
 */
const IMAGE_ACCEPT = "image/png,image/jpeg,image/webp,image/gif,image/svg+xml";

export const SKILL_CARDS_3000_CONSTANTS = {
  METADATA_TITLES: {
    metadata:
      "Containers (min 2, max 10 — first 4 visible on desktop, 3 on mobile; rest behind View More)",
  },

  METADATA_LIMITS: {
    metadata: { min: 2, max: 10 },
  },

  DEFAULT_ROW: {
    id: "1",
    meta_image: "",
    meta_image_mobile: "",
    meta_image_shadow: false,
    meta_image_shadow_color: "rgba(0,0,0,0.25)",
    meta_title: "Master Tools & People Skills",
    meta_subtitle: "",
    meta_paragraph_format: "bullets",
    meta_paragraph: "",
    meta_paragraph_color: "#000000",
    meta_icon: "",
    meta_divider: true,
    meta_divider_color: "rgba(0,0,0,0.12)",
    meta_bg_color: "#FFF3F6",
    meta_link: "",
    meta_hover_bg_color: "",
    meta_hover_text_color: "",
    meta_hover_divider_color: "",
    meta_hover_icon: "",
    meta_hover_image: "",
    meta_priority: 1,
    meta_status: true,
  },

  SKILL_CARDS_FIELDS_CONFIG: [
    {
      fieldName: "heading",
      label: "Heading (mandatory)",
      value: "Equip Yourself For What's Next",
      valueType: "string",
      filedType: "INPUT",
      required: true,
      defaultValue: "Equip Yourself For What's Next",
    },
    {
      fieldName: "sub_heading",
      label: "Sub heading (optional)",
      value: "",
      valueType: "string",
      filedType: "TEXT_AREA",
      defaultValue: "",
    },
    {
      fieldName: "heading_color",
      label: "Heading colour",
      value: "#101112",
      valueType: "string",
      filedType: "COLOR",
      defaultValue: "#101112",
    },
    {
      fieldName: "sub_heading_color",
      label: "Sub heading colour",
      value: "#101112",
      valueType: "string",
      filedType: "COLOR",
      defaultValue: "#101112",
    },
    {
      fieldName: "cta_label",
      label: "Closing CTA label (empty = no CTA)",
      value: "Apply Now",
      valueType: "string",
      filedType: "INPUT",
      defaultValue: "Apply Now",
    },
    {
      fieldName: "cta_action",
      label: "CTA click action",
      value: "apply",
      valueType: "string",
      filedType: "SELECT",
      defaultValue: "apply",
      className: "h-9 py-1.5 text-sm",
      iconClassName: "!size-4",
      options: [
        { label: "Apply (registration form)", value: "apply" },
        { label: "Download (brochure form)", value: "download" },
      ],
    },
    {
      fieldName: "cta_icon",
      label: "CTA icon (optional — defaults to the export arrow)",
      value: "",
      valueType: "string",
      filedType: "FILE",
      placeholder: "Upload an icon for the closing CTA",
      defaultValue: "",
      accept: IMAGE_ACCEPT,
    },
    {
      fieldName: "cta_url",
      label: "CTA link (overrides the click action when set)",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "e.g. https://…, /page, or #section — empty = use the click action",
      defaultValue: "",
    },
    {
      fieldName: "status",
      label: "Section status",
      value: true,
      valueType: "boolean",
      filedType: "TOGGLE",
      defaultValue: true,
    },
  ],

  METADATA_FIELDS: [
    {
      header: "Image (mandatory)",
      fieldName: "meta_image",
      value: "",
      valueType: "string",
      filedType: "FILE",
      placeholder: "Container image",
      required: true,
      defaultValue: "",
      accept: IMAGE_ACCEPT,
    },
    {
      header: "Image — mobile (mandatory)",
      fieldName: "meta_image_mobile",
      value: "",
      valueType: "string",
      filedType: "FILE",
      placeholder: "Separate creative for the stacked mobile card",
      required: true,
      defaultValue: "",
      accept: IMAGE_ACCEPT,
    },
    {
      header: "Image shadow",
      fieldName: "meta_image_shadow",
      value: false,
      valueType: "boolean",
      filedType: "TOGGLE",
      defaultValue: false,
    },
    {
      header: "Shadow colour (only shadow control)",
      fieldName: "meta_image_shadow_color",
      value: "rgba(0,0,0,0.25)",
      valueType: "string",
      filedType: "COLOR",
      defaultValue: "rgba(0,0,0,0.25)",
    },
    {
      header: "Title (one of title/subtitle/paragraph required)",
      fieldName: "meta_title",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      defaultValue: "",
    },
    {
      header: "Subtitle",
      fieldName: "meta_subtitle",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      defaultValue: "",
    },
    {
      header: "Paragraph format",
      fieldName: "meta_paragraph_format",
      value: "bullets",
      valueType: "string",
      filedType: "SELECT",
      defaultValue: "bullets",
      className: "h-9 py-1.5 text-sm",
      iconClassName: "!size-4",
      options: [
        { label: "Bullets (min 2, max 5 — one per line)", value: "bullets" },
        { label: "Paragraph (max 6 lines)", value: "paragraph" },
        { label: "Icon + lines (min 1, max 4 — one per line)", value: "icon_lines" },
      ],
    },
    {
      header: "Paragraph content (bullets/icon lines: one per line)",
      fieldName: "meta_paragraph",
      value: "",
      valueType: "string",
      filedType: "TEXTAREA",
      defaultValue: "",
      // PRD per-format ranges: bullets 2–5 · icon+lines 1–4 · paragraph ≤6
      // lines. Empty stays allowed — the "one of title/subtitle/paragraph"
      // rule is enforced by the widget, not per-field.
      validation: (value: any, row?: Record<string, any>) => {
        const lines = String(value ?? "")
          .split(/\r?\n/)
          .map((s: string) => s.trim())
          .filter(Boolean);
        if (lines.length === 0) return null;
        const format = String(row?.meta_paragraph_format ?? "bullets");
        if (format === "bullets") {
          if (lines.length < 2) return "Bullets format needs at least 2 lines (one bullet per line)";
          if (lines.length > 5) return "Bullets format allows at most 5 lines";
        } else if (format === "icon_lines") {
          if (lines.length > 4) return "Icon + lines format allows at most 4 lines";
        } else if (format === "paragraph") {
          if (lines.length > 6) return "Paragraph format allows at most 6 lines";
        }
        return null;
      },
    },
    {
      header: "Paragraph text colour",
      fieldName: "meta_paragraph_color",
      value: "#000000",
      valueType: "string",
      filedType: "COLOR",
      defaultValue: "#000000",
    },
    {
      header: "Line icon (Icon + lines format)",
      fieldName: "meta_icon",
      value: "",
      valueType: "string",
      filedType: "FILE",
      placeholder: "Icon shown before each line",
      defaultValue: "",
      accept: IMAGE_ACCEPT,
    },
    {
      header: "Divider",
      fieldName: "meta_divider",
      value: true,
      valueType: "boolean",
      filedType: "TOGGLE",
      defaultValue: true,
    },
    {
      header: "Divider colour",
      fieldName: "meta_divider_color",
      value: "rgba(0,0,0,0.12)",
      valueType: "string",
      filedType: "COLOR",
      defaultValue: "rgba(0,0,0,0.12)",
    },
    {
      header: "Container background",
      fieldName: "meta_bg_color",
      value: "#FFF3F6",
      valueType: "string",
      filedType: "COLOR",
      defaultValue: "#FFF3F6",
    },
    {
      header: "Link (page path or URL — whole card clickable)",
      fieldName: "meta_link",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      defaultValue: "",
    },
    {
      header: "Hover BG (desktop only)",
      fieldName: "meta_hover_bg_color",
      value: "",
      valueType: "string",
      filedType: "COLOR",
      defaultValue: "",
    },
    {
      header: "Hover text colour (desktop only)",
      fieldName: "meta_hover_text_color",
      value: "",
      valueType: "string",
      filedType: "COLOR",
      defaultValue: "",
    },
    {
      header: "Hover divider colour (desktop only)",
      fieldName: "meta_hover_divider_color",
      value: "",
      valueType: "string",
      filedType: "COLOR",
      defaultValue: "",
    },
    {
      header: "Hover icon (Icon + lines only)",
      fieldName: "meta_hover_icon",
      value: "",
      valueType: "string",
      filedType: "FILE",
      defaultValue: "",
      accept: IMAGE_ACCEPT,
    },
    {
      header: "Hover image",
      fieldName: "meta_hover_image",
      value: "",
      valueType: "string",
      filedType: "FILE",
      defaultValue: "",
      accept: IMAGE_ACCEPT,
    },
    {
      header: "Priority",
      fieldName: "meta_priority",
      value: 1,
      valueType: "number",
      filedType: "INPUT",
      defaultValue: 1,
    },
    {
      header: "Status",
      fieldName: "meta_status",
      value: true,
      valueType: "boolean",
      filedType: "TOGGLE",
      defaultValue: true,
    },
  ],
};
